import React, { useState, useEffect } from 'react';

const BACKEND_URL = 'https://web3-pos-v2.gdbok.workers.dev';

export const AnalyticsDashboard = ({ merchantWallet, onBack }) => {
  const [dateRange, setDateRange] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [tokenPerformance, setTokenPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [showTransactions, setShowTransactions] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const getDateRange = () => {
    const formatDate = (date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const todayUTC = new Date(Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate()
    ));

    switch (dateRange) {
      case 'today':
        return { from: formatDate(todayUTC), to: formatDate(todayUTC) };
      case '7days': {
        const weekAgo = new Date(todayUTC);
        weekAgo.setUTCDate(weekAgo.getUTCDate() - 7);
        return { from: formatDate(weekAgo), to: formatDate(todayUTC) };
      }
      case '30days': {
        const monthAgo = new Date(todayUTC);
        monthAgo.setUTCDate(monthAgo.getUTCDate() - 30);
        return { from: formatDate(monthAgo), to: formatDate(todayUTC) };
      }
      case 'custom':
        return { from: customFrom, to: customTo };
      default:
        return { from: formatDate(todayUTC), to: formatDate(todayUTC) };
    }
  };

  const loadAnalytics = async () => {
    const { from, to } = getDateRange();
    
    if (!from || !to) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Load analytics
      const analyticsResponse = await fetch(
        `${BACKEND_URL}/api/merchant/${merchantWallet}/analytics?from=${from}&to=${to}`
      );
      
      if (!analyticsResponse.ok) {
        throw new Error('Failed to load analytics');
      }

      const analyticsData = await analyticsResponse.json();
      setAnalytics(analyticsData.aggregated);

      // Load token performance
      const performanceResponse = await fetch(
        `${BACKEND_URL}/api/merchant/${merchantWallet}/token-performance?from=${from}&to=${to}`
      );
      
      if (performanceResponse.ok) {
        const performanceData = await performanceResponse.json();
        setTokenPerformance(performanceData.tokens || []);
      }

      // Load individual transactions
      const historyResponse = await fetch(
        `${BACKEND_URL}/api/merchant/${merchantWallet}/history?from=${from}&to=${to}&limit=500`
      );
      
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setTransactions(historyData.payments || []);
      }

    } catch (err) {
      console.error('Load analytics error:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // ✅ SIMPLIFIED: Use stored decimal amounts directly
  const formatAmount = (decimalAmount) => {
    try {
      return parseFloat(decimalAmount).toFixed(2);
    } catch (e) {
      return '0.00';
    }
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBlockExplorer = (chainId, txHash) => {
    const explorers = {
      137: `https://polygonscan.com/tx/${txHash}`,
      56: `https://bscscan.com/tx/${txHash}`,
      43114: `https://snowtrace.io/tx/${txHash}`,
      10: `https://optimistic.etherscan.io/tx/${txHash}`
    };
    return explorers[chainId] || '#';
  };

const exportToCSV = () => {
  // ✅ Updated headers to include Fiat data
  const headers = ['Date', 'Time', 'Terminal ID', 'POS ID', 'Fiat Amount', 'Fiat Currency', 'Crypto Amount', 'Token', 'Conversion Rate', 'Source Chain', 'Transaction Ref', 'TX Hash'];
  
  const rows = transactions.map(tx => {
    const date = new Date(tx.confirmedAt);
    return [
      date.toLocaleDateString(),
      date.toLocaleTimeString(),
      tx.tokenId || 'Unknown',
      tx.posId || 'N/A',
      // ✅ Fiat data
      tx.fiatAmount ? formatAmount(tx.fiatAmount) : 'N/A',
      tx.fiatCurrency || 'N/A',
      // ✅ Crypto data
      formatAmount(tx.decimalAmount),
      tx.genericToken || 'Unknown',
      // ✅ Conversion rate
      tx.conversionRateString ? parseFloat(tx.conversionRateString).toFixed(2) : 'N/A',
      tx.chainName || 'Unknown',
      tx.txRef,
      tx.txHash || 'N/A'
    ];
  });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${getDateRange().from}_to_${getDateRange().to}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px',
      paddingBottom: '40px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      minHeight: '100vh',
      overflow: 'auto'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <button
            onClick={onBack}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              background: 'white',
              color: '#6366f1',
              border: '1px solid #6366f1',
              borderRadius: '8px',
              cursor: 'pointer',
              marginBottom: '12px'
            }}
          >
            ← Back to Tokens
          </button>
          <h1 style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>
            📊 Analytics Dashboard
          </h1>
        </div>
      </div>

      {/* Date Range Selector */}
      <div style={{
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => setDateRange('today')}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              background: dateRange === 'today' ? '#6366f1' : 'white',
              color: dateRange === 'today' ? 'white' : '#6b7280',
              border: '1px solid ' + (dateRange === 'today' ? '#6366f1' : '#d1d5db'),
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Today
          </button>
          <button
            onClick={() => setDateRange('7days')}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              background: dateRange === '7days' ? '#6366f1' : 'white',
              color: dateRange === '7days' ? 'white' : '#6b7280',
              border: '1px solid ' + (dateRange === '7days' ? '#6366f1' : '#d1d5db'),
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Last 7 Days
          </button>
          <button
            onClick={() => setDateRange('30days')}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              background: dateRange === '30days' ? '#6366f1' : 'white',
              color: dateRange === '30days' ? 'white' : '#6b7280',
              border: '1px solid ' + (dateRange === '30days' ? '#6366f1' : '#d1d5db'),
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Last 30 Days
          </button>
          <button
            onClick={() => setDateRange('custom')}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              background: dateRange === 'custom' ? '#6366f1' : 'white',
              color: dateRange === 'custom' ? 'white' : '#6b7280',
              border: '1px solid ' + (dateRange === 'custom' ? '#6366f1' : '#d1d5db'),
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Custom
          </button>

          {dateRange === 'custom' && (
            <>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px'
                }}
              />
              <span style={{ color: '#6b7280' }}>to</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px'
                }}
              />
              <button
                onClick={loadAnalytics}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: '#6366f1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Apply
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#991b1b',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #fecaca'
        }}>
          ⚠️ {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          Loading analytics...
        </div>
      ) : analytics ? (
        <>
          {/* Summary Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              padding: '20px',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                Total Transactions
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700' }}>
                {analytics.totalTransactions || 0}
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '20px',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                Active Tokens
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700' }}>
                {Object.keys(analytics.byToken || {}).length}
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '20px',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                Payment Methods
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700' }}>
                {Object.keys(analytics.byChainAndToken || {}).length}
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
              color: 'white',
              padding: '20px',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                Source Chains
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700' }}>
                {Object.keys(analytics.bySources || {}).length}
              </div>
            </div>
          </div>

          {/* Revenue by Chain & Token */}
          {analytics.byChainAndToken && Object.keys(analytics.byChainAndToken).length > 0 && (
            <div style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
                Revenue by Chain & Token
              </h2>
              <div style={{ display: 'grid', gap: '12px' }}>
             
     
              {Object.entries(analytics.byChainAndToken)
  .sort((a, b) => b[1].count - a[1].count)
  .map(([key, data]) => (
    <div
      key={key}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        background: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}
    >
      <div>
        <div style={{ fontWeight: '600', marginBottom: '4px' }}>
          {data.chainName} - {data.genericToken || 'TOKEN'}
        </div>
        <div style={{ fontSize: '13px', color: '#6b7280' }}>
          {data.count} transactions
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        {/* ✅ Show both Fiat and Crypto amounts if available */}
        {data.totalFiatAmount && data.fiatCurrency ? (
          <>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#6366f1' }}>
              {data.fiatCurrency === 'EUR' ? '€' : '$'}{formatAmount(data.totalFiatAmount)}
              <span style={{ fontSize: '14px', fontWeight: '500', marginLeft: '4px', opacity: 0.7 }}>
                {data.fiatCurrency}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              ≈ ${formatAmount(data.totalAmount)} {data.genericToken}
            </div>
          </>
        ) : (
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#6366f1' }}>
            ${formatAmount(data.totalAmount)}
          </div>
        )}
      </div>
    </div>
  ))}
              
     
              
              
              </div>
            </div>
          )}

          {/* Token Performance */}
          {tokenPerformance.length > 0 && (
            <div style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
                Token Performance
              </h2>
              <div style={{ display: 'grid', gap: '12px' }}>
          
          
          
          
              {tokenPerformance.map(token => (
  <div
    key={token.tokenId}
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px',
      background: '#f9fafb',
      borderRadius: '8px',
      border: '1px solid #e5e7eb'
    }}
  >
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <span style={{ fontWeight: '600' }}>{token.name}</span>
        <span style={{
          background: '#dbeafe',
          color: '#1e40af',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '600',
          fontFamily: 'monospace'
        }}>
          {token.humanCode}
        </span>
      </div>
      <div style={{ fontSize: '13px', color: '#6b7280' }}>
        {token.transactions} transactions • {token.genericToken || 'TOKEN'}
        {token.fiatCurrency && (
          <span> • {token.fiatCurrency}</span>
        )}
      </div>
    </div>
    <div style={{ textAlign: 'right' }}>
      {/* ✅ Show both Fiat and Crypto revenue if available */}
      {token.fiatRevenue && token.fiatCurrency ? (
        <>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#10b981' }}>
            {token.fiatCurrency === 'EUR' ? '€' : '$'}{formatAmount(token.fiatRevenue)}
            <span style={{ fontSize: '12px', fontWeight: '500', marginLeft: '4px', opacity: 0.7 }}>
              {token.fiatCurrency}
            </span>
          </div>
          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
            ≈ ${formatAmount(token.revenue)} {token.genericToken}
          </div>
        </>
      ) : (
        <div style={{ fontSize: '18px', fontWeight: '700', color: '#10b981' }}>
          ${formatAmount(token.revenue)}
        </div>
      )}
    </div>
  </div>
))}
            
            
            
            
            
              </div>
            </div>
          )}

          {/* Transaction History Section */}
          <div style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                Transaction History
              </h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                {transactions.length > 0 && (
                  <button
                    onClick={() => exportToCSV()}
                    style={{
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      background: 'white',
                      color: '#10b981',
                      border: '1px solid #10b981',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    📥 Export CSV
                  </button>
                )}
                <button
                  onClick={() => setShowTransactions(!showTransactions)}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    background: 'white',
                    color: '#6366f1',
                    border: '1px solid #6366f1',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  {showTransactions ? 'Hide Transactions' : `Show All (${transactions.length})`}
                </button>
              </div>
            </div>

            {showTransactions && transactions.length > 0 ? (
              <div style={{
                maxHeight: '600px',
                overflowY: 'auto',
                marginTop: '16px'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px'
                }}>
                  <thead>
                    <tr style={{
                      background: '#f9fafb',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151'
                      }}>Date & Time</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151'
                      }}>Terminal</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151'
                      }}>Amount</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151'
                      }}>Chain</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151'
                      }}>Ref</th>
                    </tr>
                  </thead>

                  <tbody>
                    {transactions.map((tx, index) => (
                      <tr
                        key={tx.txRef}
                        style={{
                          borderBottom: '1px solid #e5e7eb',
                          background: index % 2 === 0 ? 'white' : '#f9fafb'
                        }}
                      >
                        <td style={{ padding: '12px', color: '#6b7280' }}>
                          {formatDateTime(tx.confirmedAt)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontWeight: '500', color: '#374151' }}>
                            {tx.tokenId?.substring(0, 8) || 'Unknown'}
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>
                            {tx.posId || 'N/A'}
                          </div>
                        </td>
                       
                       
                       
                       <td style={{ padding: '12px' }}>
  {/* ✅ Show both Fiat and Crypto amounts */}
  {tx.fiatAmount && tx.fiatCurrency ? (
    <>
      <div style={{ fontWeight: '600', color: '#10b981' }}>
        {tx.fiatCurrency === 'EUR' ? '€' : '$'}{formatAmount(tx.fiatAmount)}
        <span style={{ fontSize: '11px', fontWeight: '400', marginLeft: '4px', opacity: 0.7 }}>
          {tx.fiatCurrency}
        </span>
      </div>
      <div style={{ fontSize: '11px', color: '#6b7280' }}>
        ≈ ${formatAmount(tx.decimalAmount)} {tx.genericToken || 'TOKEN'}
      </div>
    </>
  ) : (
    <>
      <div style={{ fontWeight: '600', color: '#10b981' }}>
        ${formatAmount(tx.decimalAmount)}
      </div>
      <div style={{ fontSize: '11px', color: '#6b7280' }}>
        {tx.genericToken || 'TOKEN'}
      </div>
    </>
  )}
</td>
                        
                        
                        
                        
                        
                        
                        <td style={{ padding: '12px' }}>
                          <div style={{ color: '#374151' }}>
                            {/* ✅ SIMPLIFIED: Use stored chainName directly */}
                            {tx.chainName || 'Unknown'}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            color: '#6b7280',
                            maxWidth: '120px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {tx.txRef}
                          </div>
                          {tx.txHash && (
                            <a
                              href={getBlockExplorer(tx.sourceChain, tx.txHash)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: '11px',
                                color: '#6366f1',
                                textDecoration: 'none'
                              }}
                            >
                              View TX ↗
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : showTransactions && transactions.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#6b7280'
              }}>
                No transactions found for this period
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: '#f9fafb',
          borderRadius: '12px',
          border: '2px dashed #d1d5db'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            No data available
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            No transactions found for the selected period
          </p>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;